import { z } from 'zod';

import type { BadgeTone } from '@/components/ui/Badge';
import {
  dateOnlySchema,
  emailInputSchema,
  mobileInputSchema,
  optionalId,
  optionalInput,
  optionalText,
  staffRefSchema,
  timestampSchema,
} from '@/types/schemas';

// --- Enums (mirror prisma/schema.prisma in the backend) ---------------------------------

/** `frozen`: on a break — a freeze of their membership covers today. Set only by the API. */
export const MEMBER_STATUSES = ['active', 'frozen', 'expired', 'suspended', 'inactive'] as const;
export type MemberStatus = (typeof MEMBER_STATUSES)[number];

/** What staff may pick on the member form; the others follow memberships (and freezes). */
export const EDITABLE_MEMBER_STATUSES = MEMBER_STATUSES.filter(
  (status): status is Exclude<MemberStatus, 'frozen'> => status !== 'frozen',
);

export const MEMBER_STATUS_LABEL: Record<MemberStatus, string> = {
  active: 'Active',
  frozen: 'Frozen',
  expired: 'Expired',
  suspended: 'Suspended',
  inactive: 'Inactive',
};

export const MEMBER_STATUS_TONE: Record<MemberStatus, BadgeTone> = {
  active: 'success',
  frozen: 'info',
  expired: 'warning',
  suspended: 'danger',
  inactive: 'neutral',
};

export const GENDERS = ['male', 'female', 'other'] as const;
export type Gender = (typeof GENDERS)[number];

export const GENDER_LABEL: Record<Gender, string> = {
  male: 'Male',
  female: 'Female',
  other: 'Other',
};

// --- API responses --------------------------------------------------------------------

export const memberSchema = z.object({
  id: z.number(),
  memberCode: z.string(),
  firstName: z.string(),
  lastName: z.string().nullable(),
  mobile: z.string(),
  email: z.string().nullable(),
  dateOfBirth: dateOnlySchema.nullable(),
  gender: z.enum(GENDERS).nullable(),
  photoPath: z.string().nullable(),
  emergencyContactName: z.string().nullable(),
  emergencyContactMobile: z.string().nullable(),
  address: z.string().nullable(),
  status: z.enum(MEMBER_STATUSES),
  /** The staff member this one counts as. Null for everyone added before the field existed. */
  owner: staffRefSchema.nullable(),
  createdAt: timestampSchema,
  updatedAt: timestampSchema,
});
export type Member = z.infer<typeof memberSchema>;

// --- Forms (same rules as the API) --------------------------------------------------------

export const memberFormSchema = z.object({
  firstName: z.string().trim().min(1, 'Enter a first name').max(100, 'Use at most 100 characters'),
  lastName: optionalText(100),
  mobile: mobileInputSchema,
  email: optionalInput(emailInputSchema),
  // Both are required, like the API: the profile shows the age and the gender under the photo.
  dateOfBirth: z.string().trim().min(1, 'Enter the date of birth').pipe(dateOnlySchema),
  gender: z.enum(GENDERS, { error: 'Choose a gender' }),
  emergencyContactName: optionalText(150),
  emergencyContactMobile: optionalInput(mobileInputSchema),
  address: optionalText(2000),
  status: z.enum(MEMBER_STATUSES),
  /** Picked from the staff list; blank means nobody is credited. */
  ownerId: optionalId,
  // No member code: the API always numbers members itself (M-00001).
});
export type MemberFormOutput = z.output<typeof memberFormSchema>;

/** What the API accepts on create and update. */
export type MemberInput = MemberFormOutput;

/** Creating a member's app login (`POST /members/:id/account`). */
export const memberAccountFormSchema = z
  .object({
    mobile: optionalInput(mobileInputSchema),
    email: optionalInput(emailInputSchema),
    password: optionalInput(
      z.string().min(8, 'Use at least 8 characters').max(128, 'Use at most 128 characters'),
    ),
  })
  .refine((values) => values.mobile !== null || values.email !== null, {
    path: ['mobile'],
    message: 'A login needs a mobile number or an email',
  });
export type MemberAccountFormOutput = z.output<typeof memberAccountFormSchema>;
